// src/hooks/usePolishAudio.ts
import {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { BASE_API_URL } from '../services/ApiService';
import { cleanTextForTTS } from '../utils/textBracketSanitize';
import { notifyAudioError, watchAudioStart } from '../utils/audioErrors';

const STORAGE_KEY = 'playPolish';

interface UsePolishAudioOptions {
  storageKey?: string;
  // Stan PL przy pierwszym wejściu do gry, gdy przeglądarka nie ma jeszcze nic zapisanego
  defaultOn?: boolean;
}

export const usePolishAudio = (options?: UsePolishAudioOptions) => {
  const storageKey = options?.storageKey || STORAGE_KEY;
  const defaultOn = options?.defaultOn ?? true;

  const [playPolish, setPlayPolish] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(storageKey);

      return saved === null ? defaultOn : saved === 'true';
    } catch {
      return defaultOn;
    }
  });

  const playPolishRef = useRef(playPolish);
  const polishAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    playPolishRef.current = playPolish;
    try {
      localStorage.setItem(storageKey, String(playPolish));
    } catch { /* ignore storage errors */ }
  }, [playPolish, storageKey]);

  const stopPolishAudio = useCallback(() => {
    if (polishAudioRef.current) {
      try {
        polishAudioRef.current.pause();
        polishAudioRef.current.currentTime = 0;
        polishAudioRef.current.onended = null;
        polishAudioRef.current.onerror = null;
      } catch { /* ignore */ }
      polishAudioRef.current = null;
    }
  }, []);

  // Czyta po polsku bez względu na przełącznik PL – np. gdy gracz sam kliknie głośnik na obrazku
  const forcePlayPolishAudio = useCallback((text: string, onEnded?: () => void) => {
    // Wyczyść tekst
    const cleanText = cleanTextForTTS(text);

    // Jeśli tekst jest pusty (np. był tylko w nawiasie), wykonaj callback
    if (!cleanText) {
      if (onEnded) onEnded();

      return;
    }

    stopPolishAudio();

    const polishUrl = `${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanText)}&lang=pl`;
    const newPolishAudio = new Audio(polishUrl);
    polishAudioRef.current = newPolishAudio;

    // Koniec odtwarzania albo błąd – w obu przypadkach gra idzie dalej, żeby nie utknęła.
    // Pomijamy audio zatrzymane przez stopPolishAudio() albo zastąpione nowym.
    let finished = false;
    const finish = (failed: boolean, error?: unknown) => {
      if (finished || polishAudioRef.current !== newPolishAudio) return;
      finished = true;
      if (failed) notifyAudioError(error);
      if (onEnded) onEnded();
    };

    newPolishAudio.onended = () => finish(false);
    newPolishAudio.onerror = () => finish(true);

    const playPromise = newPolishAudio.play();
    watchAudioStart(newPolishAudio, playPromise, () => finish(true));
    playPromise.catch((error) => finish(true, error));
  }, [stopPolishAudio]); // Zależność od `cleanTextForTTS` nie jest potrzebna (pure function)

  // ZMIANA: Cała funkcja została zaktualizowana
  const playPolishAudioFn = useCallback((text: string, onEnded?: () => void) => {
    // Jeśli polskie audio jest wyłączone, natychmiast wykonaj callback
    if (!playPolishRef.current) {
      if (onEnded) onEnded();

      return;
    }

    forcePlayPolishAudio(text, onEnded);
  }, [forcePlayPolishAudio]);

  const togglePlayPolish = useCallback(() => {
    setPlayPolish((prev) => !prev);
  }, []);

  useEffect(() => () => {
    stopPolishAudio();
  }, [stopPolishAudio]);

  return {
    playPolish,
    setPlayPolish,
    togglePlayPolish,
    playPolishAudio: playPolishAudioFn,
    forcePlayPolishAudio,
    stopPolishAudio,
  };
};
